/**
 * Key-mirror: grant this browser ALL of the phone's pairings by scanning a QR — no re-pairing each device.
 *
 * Flow (see the Web UI idea): the browser makes an ephemeral ECDH keypair and shows a QR carrying its public
 * key + a random relay id. The phone scans it, derives the SAME shared secret (its own ephemeral ECDH ⋈ our
 * public), AES-GCM-encrypts the bundle of all its pairing secrets, and drops ONE short-lived ciphertext doc at
 * `keyMirror/{relayId}`. We read it, decrypt with our private key, import the pairings, and delete the doc.
 * Firebase only ever sees ciphertext; the ephemeral private key never leaves the tab (non-extractable), so a
 * captured relay doc is useless. Handshake MUST match the Android side (Crypto.kt / PhoneEngine.mirrorTo):
 *   curve P-256 · pub = raw uncompressed point (65 B) base64url · AES key = SHA-256(ECDH shared X) ·
 *   ciphertext = the shared AES-GCM wire format base64(iv‖ct‖tag).
 */
import { bytesToB64url, b64urlToBytes, utf8ToBytes } from "@shared/encoding";
import { decrypt as aesDecrypt } from "./crypto";
import type { PairingPayload } from "./pairings";

/** Marks a key-mirror offer (distinct from the `cc1:` pairing key the phone also scans). */
export const MIRROR_QR_PREFIX = "ccmirror1:";

/** The QR encodes an App-Link deep link so scanning with the phone's camera opens the Failover app
 *  straight into the mirror flow (matches the pairing link's `app.failover.dev/pair#…` pattern). The
 *  payload rides in the fragment, so it never reaches any server. */
const MIRROR_LINK_BASE = "https://app.failover.dev/mirror#";

export interface MirrorOffer {
  /** Our ephemeral ECDH keypair (private key non-extractable, lives only in this tab). */
  keypair: CryptoKeyPair;
  /** Random relay doc id the phone will write the encrypted bundle to. */
  relayId: string;
  /** The `ccmirror1:…` string to render as a QR for the phone to scan. */
  qr: string;
}

/** Mint a fresh mirror offer: ephemeral keypair + relay id + the QR payload. */
export async function createMirrorOffer(): Promise<MirrorOffer> {
  const keypair = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, false, ["deriveBits"]);
  const rawPub = new Uint8Array(await crypto.subtle.exportKey("raw", keypair.publicKey));
  const relayId = crypto.randomUUID().replace(/-/g, "");
  const payload = MIRROR_QR_PREFIX + bytesToB64url(utf8ToBytes(JSON.stringify({ pub: bytesToB64url(rawPub), relay: relayId })));
  const qr = MIRROR_LINK_BASE + payload;
  return { keypair, relayId, qr };
}

/** Derive the shared AES-GCM key from our private key + the phone's raw uncompressed P-256 public key. */
async function deriveMirrorKey(webPriv: CryptoKey, phonePubRaw: Uint8Array): Promise<CryptoKey> {
  const phonePub = await crypto.subtle.importKey(
    "raw",
    phonePubRaw as BufferSource,
    { name: "ECDH", namedCurve: "P-256" },
    false,
    [],
  );
  const sharedBits = await crypto.subtle.deriveBits({ name: "ECDH", public: phonePub }, webPriv, 256);
  const aesRaw = await crypto.subtle.digest("SHA-256", sharedBits);
  return crypto.subtle.importKey("raw", aesRaw, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
}

/** Decode the phone's relay doc `{pub, ct}` back into the mirrored pairings (or null if it's not decryptable). */
export async function decodeMirrorDoc(
  offer: MirrorOffer,
  data: { pub?: unknown; ct?: unknown },
): Promise<PairingPayload[] | null> {
  if (typeof data.pub !== "string" || typeof data.ct !== "string") return null;
  const key = await deriveMirrorKey(offer.keypair.privateKey, b64urlToBytes(data.pub));
  const json = await aesDecrypt(key, data.ct);
  if (!json) return null;
  try {
    const arr = JSON.parse(json) as unknown;
    if (!Array.isArray(arr)) return null;
    return arr.filter(
      (p): p is PairingPayload =>
        !!p && typeof (p as PairingPayload).pairingId === "string" && typeof (p as PairingPayload).pairingSecret === "string",
    );
  } catch {
    return null;
  }
}
